import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/teachers/TeachersAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  SuccessButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { TeachersFilter } from "./teachers-filter/TeachersFilter";
import { TeachersTable } from "./teachers-table/TeachersTable";
import { useTeachersUIContext } from "./TeachersUIContext";

export function TeachersCard() {
  // Teachers UI Context
  const teachersUIContext = useTeachersUIContext();
  const teachersUIProps = useMemo(() => {
    return {
      ids: teachersUIContext.ids,
      setIds: teachersUIContext.setIds,
      queryParams: teachersUIContext.queryParams,
      openNewTeacherPage: teachersUIContext.openNewTeacherPage,
    };
  }, [teachersUIContext]);
    
  // Getting curret state of Teacher list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.teachers }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;
  
  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Teachers Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğretmen Listesini Excele Aktarır */
  const exportTeachersToExcel = () => {
    dispatch(actions.getAllTeachers(teachersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [20, 30, 30, 30, 15];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Branşı"];
      
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.branch
      ]));
      
      const filename = teachersUIProps.queryParams.term + " - " + (teachersUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğretmen Listesi";
      const sheetname = "Öğretmen Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğretmen Listesini PDF e Aktarır */
  const exportTeachersToPDF = () => {
    dispatch(actions.getAllTeachers(teachersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Branşı",
          rows: entities.map(entity => entity.branch)
        }
      ];
  
      const filename = teachersUIProps.queryParams.term + " - " + (teachersUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğretmen Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğretmen Listesini Yazdırma Penceresini Açar */
  const printTeachers = () => {
    dispatch(actions.getAllTeachers(teachersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = teachersUIProps.queryParams.term + " - " + (teachersUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğretmen Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Branşı"];
  
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Branşı": entity.branch
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Öğretmen Listesini Panoya Kopyalar */
  const copyTeachers = () => {
    dispatch(actions.getAllTeachers(teachersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Branşı"];
      const header = teachersUIProps.queryParams.term + " - " + (teachersUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğretmen Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.branch
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Öğretmen Listesinin Sözleşmelerini Yeniler */
  const renewContracts = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
  
    dispatch(actions.renewContracts(ids)).then(result => onResult(result));
  }

  /** Belli Bir Öğretmen Listesinin Sözleşmelerini Siler */
  const deleteContracts = (term, ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteContracts(term, ids)).then(result => onResult(result));
    });
  }
  
  /** Belli Bir Öğretmen Listesini Siler */
  const deleteTeachers = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteTeachers(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        teachersUIProps.setIds([]);
        dispatch(actions.getTeachers(teachersUIProps.queryParams));
      }, 2500);
    }
  };
  
  const title = <>Öğretmen Listesi{teachersUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{teachersUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportTeachersToExcel}
                exportToPDF={exportTeachersToPDF}
                print={printTeachers}
                copy={copyTeachers}
              />
            }
            {
              isAdditionAuthorized &&
              <SuccessButton
                path="Code/Loading"
                title="Sözleşmeleri Yenile"
                method={() => renewContracts(teachersUIProps.ids)}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Sözleşmeleri Sil"
                method={() => deleteContracts(teachersUIProps.queryParams.term, teachersUIProps.ids)}
              />
            }
            {
              isAdditionAuthorized && 
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Öğretmen"
                method={teachersUIProps.openNewTeacherPage}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deleteTeachers(teachersUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <TeachersFilter />
        <TeachersTable
          renewContracts={renewContracts}
          deleteContracts={deleteContracts}
          deleteTeachers={deleteTeachers}
        />
      </CardBody>
    </Card>
  );
}